import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Check, ExternalLink, ShieldAlert, Loader2, Clock, CircleAlert } from "lucide-react";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const fmtCountdown = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return "00:00";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard unavailable */ }
      }}
      data-testid={testid}
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#2E6BFF]/40 text-[#8FB8E8] text-[11px] font-mono font-bold uppercase tracking-widest hover:bg-[#2E6BFF]/10 transition-all"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function CryptoPay() {
  const [params] = useSearchParams();
  const orderId = params.get("order");
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", data: null });
  const [countdown, setCountdown] = useState(null);
  const [extending, setExtending] = useState(false);
  const paid = useRef(false);

  const extendQuote = async () => {
    setExtending(true);
    try {
      const { data } = await api.post(`/payments/crypto/extend/${orderId}`);
      setState({ phase: "waiting", data });
      toast.success("Fresh quote locked — 30 more minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend this quote");
    } finally {
      setExtending(false);
    }
  };

  useEffect(() => {
    if (!orderId) {
      setState({ phase: "error", data: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/crypto/status/${orderId}`);
        if (cancelled) return;
        if (data.status === "paid") {
          if (!paid.current) {
            paid.current = true;
            navigate(`/payment/success?order=${orderId}`, { replace: true });
          }
          return;
        }
        setState({ phase: data.status === "expired" ? "expired" : "waiting", data });
      } catch (e) {
        if (!cancelled && e?.response?.status === 404) setState({ phase: "error", data: null });
      }
      if (!cancelled) setTimeout(poll, 5000);
    };
    poll();
    return () => { cancelled = true; };
  }, [orderId, navigate]);

  const expiresAt = state.data?.expires_at;
  useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = () => setCountdown(fmtCountdown(expiresAt));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const d = state.data;
  const detected = !!d?.txid;
  const windowOver = countdown === "00:00";

  return (
    <div className="min-h-screen bg-[#050B18]" data-testid="crypto-pay-page">
      <Navbar />
      <div className="pt-28 pb-20 px-6">
        <div className="max-w-lg mx-auto">
          {state.phase === "loading" && (
            <div className="flex items-center justify-center gap-3 py-24 text-slate-400" data-testid="crypto-loading">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading your payment…
            </div>
          )}

          {state.phase === "error" && (
            <div className="p-5 rounded-xl bg-[#0A1628] border border-[#1E2D4A] text-sm text-slate-400" data-testid="crypto-error">
              We couldn't find that crypto order. It may have been created with the old provider —
              check <Link to="/portal" className="text-[#7FB0FF] underline underline-offset-2">your portal</Link> or contact support on Discord.
            </div>
          )}

          {state.phase === "expired" && (
            <div className="p-6 rounded-xl bg-[#0A1628] border border-amber-500/30 text-center" data-testid="crypto-expired">
              <CircleAlert className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <h1 className="font-display text-2xl font-bold text-white mb-2">This payment window has closed</h1>
              <p className="text-sm text-slate-400 leading-relaxed mb-5">
                If you already sent the payment, don't worry — it will still be detected and flagged for the team
                to fulfil manually. Otherwise grab a fresh quote — same order, live price, 30 more minutes.
              </p>
              <button
                onClick={extendQuote}
                disabled={extending}
                data-testid="crypto-extend-button"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold disabled:opacity-40 transition-all duration-200 active:scale-95 mb-4"
              >
                {extending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                {extending ? "Refreshing quote..." : "Get a fresh quote"}
              </button>
              <div>
                <Link to="/" data-testid="crypto-expired-home" className="text-[#7FB0FF] text-sm underline underline-offset-2">
                  Back to the store
                </Link>
              </div>
            </div>
          )}

          {state.phase === "waiting" && d && (
            <>
              <div className="text-center mb-6">
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-xs font-mono font-bold uppercase tracking-widest ${
                    detected
                      ? "border-sky-400/40 bg-sky-400/10 text-sky-300"
                      : "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                  }`}
                  data-testid="crypto-status-line"
                >
                  <span className="w-2 h-2 rounded-full bg-current animate-pulse" />
                  {detected
                    ? `Payment detected — confirming (${Math.min(d.confirmations, d.confirmations_required)}/${d.confirmations_required})`
                    : "Waiting for payment — updates automatically"}
                </div>
                <h1 className="font-display text-3xl font-bold tracking-tight text-white mt-4" data-testid="crypto-title">
                  Pay with {d.name} <span className="text-[#7FB0FF]">{d.symbol}</span>
                </h1>
                <p className="text-sm text-slate-500 mt-1" data-testid="crypto-network-line">
                  Network: <span className="text-slate-300 font-semibold">{d.network}</span>
                </p>
              </div>

              <div className="p-6 rounded-2xl bg-[#0A1628] border border-[#1E2D4A]">
                <div className="flex justify-center mb-5">
                  <div className="p-3 bg-white rounded-xl" data-testid="crypto-qr">
                    <QRCodeSVG value={d.payment_uri || d.address} size={200} level="M" />
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Send exactly</div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#050B18] border border-[#1E2D4A]">
                      <span className="font-mono text-lg font-bold text-white" data-testid="crypto-amount">
                        {d.expected_amount} {d.symbol}
                      </span>
                      <CopyButton text={String(d.expected_amount)} testid="crypto-copy-amount" />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">To this address</div>
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[#050B18] border border-[#1E2D4A]">
                      <span className="font-mono text-xs text-slate-300 break-all" data-testid="crypto-address">{d.address}</span>
                      <CopyButton text={d.address} testid="crypto-copy-address" />
                    </div>
                  </div>

                  {d.payment_uri && d.payment_uri !== d.address && (
                    <a
                      href={d.payment_uri}
                      data-testid="crypto-open-wallet"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#2E6BFF] hover:bg-[#1D55E0] text-white text-sm font-semibold transition-all duration-200 active:scale-95"
                    >
                      <ExternalLink className="w-4 h-4" /> Open Wallet
                    </a>
                  )}

                  <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
                    <span data-testid="crypto-fiat-line">
                      ≈ {Number(d.charge_total).toFixed(2)} {(d.currency || "aud").toUpperCase()}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 font-mono ${windowOver ? "text-amber-400" : "text-slate-400"}`} data-testid="crypto-countdown">
                      <Clock className="w-3.5 h-3.5" /> {windowOver ? "quote expired" : countdown}
                    </span>
                  </div>
                  {windowOver && (
                    <button
                      onClick={extendQuote}
                      disabled={extending}
                      data-testid="crypto-extend-inline"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-amber-400/40 text-amber-300 text-xs font-mono font-bold uppercase tracking-widest hover:bg-amber-400/10 disabled:opacity-40 transition-all"
                    >
                      {extending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
                      {extending ? "Refreshing..." : "Quote expired — get a fresh one (keeps this order)"}
                    </button>
                  )}
                </div>
              </div>

              {d.underpaid && (
                <div className="mt-4 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10 text-sm text-amber-200" data-testid="crypto-underpaid">
                  We received {d.paid_amount} {d.symbol} — a bit short of {d.expected_amount} {d.symbol}.
                  Send the remainder to the same address and this page will complete automatically.
                </div>
              )}

              <div className="mt-4 p-4 rounded-xl border border-red-500/30 bg-red-500/5 flex gap-3" data-testid="crypto-network-warning">
                <ShieldAlert className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400 leading-relaxed">
                  Only send <span className="text-white font-semibold">{d.symbol}</span> on the{" "}
                  <span className="text-white font-semibold">{d.network}</span> network. Sending any other coin or
                  using a different network can permanently lose your funds.
                </p>
              </div>

              <ol className="mt-6 space-y-2.5 text-sm text-slate-500" data-testid="crypto-steps">
                <li><span className="text-slate-300 font-mono mr-2">1.</span>Open your wallet or exchange app and choose Send / Withdraw.</li>
                <li><span className="text-slate-300 font-mono mr-2">2.</span>Scan the QR or paste the address, and enter the exact amount above.</li>
                <li><span className="text-slate-300 font-mono mr-2">3.</span>Send — this page detects the transfer and delivers your keys automatically.</li>
              </ol>

              <p className="text-center text-[11px] text-slate-600 mt-6">
                Closed this page? Find the order again in the{" "}
                <Link to="/portal" className="text-[#7FB0FF] underline underline-offset-2" data-testid="crypto-portal-link">Customer Portal</Link>.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
